import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { CheckCircle, XCircle, Wrench, Truck, RefreshCw } from 'lucide-react';

export default function ConfirmationPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action');
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, info, confirmed, refused, error, already
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const process = async () => {
      try {
        const res = await api.get(`/confirmations/${token}`);
        setInfo(res.data);

        if (res.data.status !== 'en_attente') {
          setStatus('already');
          return;
        }

        if (action === 'confirm') {
          await api.post(`/confirmations/${token}/confirm`);
          setStatus('confirmed');
        } else if (action === 'refuse') {
          await api.post(`/confirmations/${token}/refuse`);
          setStatus('refused');
        } else {
          setStatus('info');
        }
      } catch (e) {
        setErrorMsg(e.response?.data?.detail || 'Lien invalide ou expiré');
        setStatus('error');
      }
    };
    process();
  }, [token, action]);

  const handleAction = async (act) => {
    try {
      await api.post(`/confirmations/${token}/${act}`);
      setStatus(act === 'confirm' ? 'confirmed' : 'refused');
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'Erreur');
      setStatus('error');
    }
  };

  const Icon = info?.type_tache === 'intervention' ? Wrench : Truck;
  const typeLabel = info?.type_tache === 'intervention' ? 'Intervention SAV' : 'Livraison';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
        {status === 'loading' && (
          <div className="py-8">
            <RefreshCw size={32} className="animate-spin text-gray-400 mx-auto" />
            <p className="text-gray-500 mt-4">Traitement en cours...</p>
          </div>
        )}

        {status === 'confirmed' && (
          <div className="py-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Prise en charge confirmée</h2>
            <p className="text-gray-500">Merci {info?.technicien_nom} ! L'admin a été informé.</p>
            {info && (
              <div className="mt-4 p-4 bg-gray-50 rounded-xl text-left text-sm">
                <p className="font-medium text-gray-900">{info.client_societe}</p>
                <p className="text-gray-500">{info.detail}</p>
                <p className="text-gray-400 mt-1">{info.date_prevue}</p>
              </div>
            )}
          </div>
        )}

        {status === 'refused' && (
          <div className="py-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Tâche refusée</h2>
            <p className="text-gray-500">L'admin sera notifié et réassignera la tâche.</p>
          </div>
        )}

        {status === 'already' && (
          <div className="py-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon size={32} className="text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Déjà traité</h2>
            <p className="text-gray-500">Cette demande a déjà été {info?.status === 'confirmee' ? 'confirmée' : 'refusée'}.</p>
          </div>
        )}

        {status === 'info' && info && (
          <div className="py-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon size={32} className="text-orange-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{typeLabel}</h2>
            <div className="mt-4 p-4 bg-gray-50 rounded-xl text-left text-sm space-y-1">
              <p><span className="text-gray-500">Client :</span> <span className="font-medium">{info.client_societe}</span></p>
              <p><span className="text-gray-500">Détail :</span> {info.detail}</p>
              <p><span className="text-gray-500">Date :</span> {info.date_prevue}</p>
            </div>
            <div className="flex gap-3 mt-6 justify-center">
              <button onClick={() => handleAction('confirm')} className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700" data-testid="confirm-btn">
                Je confirme
              </button>
              <button onClick={() => handleAction('refuse')} className="px-6 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700" data-testid="refuse-btn">
                Indisponible
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="py-6">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-gray-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Erreur</h2>
            <p className="text-gray-500">{errorMsg}</p>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-6">IBN CRM — SAS IBN</p>
      </div>
    </div>
  );
}
